/**
 * VideoResponse
 *
 * Lets respondents select questions and upload/record a video with length
 * validation, Safari/iOS download handling, and theme-aware UI.
 */
import React from 'react';
import { Video, X } from 'lucide-react';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';

// Helper to extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  // Handle various YouTube URL formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

export function VideoResponse({ 
  campaign, 
  currentQuestion, 
  setCurrentQuestion, 
  formData, 
  handleVideoUpload, 
  handleRemoveVideo, 
  videoDuration,
  theme, 
  themes,
  customTheme,
  getTextStyle,
  getSubtextStyle,
  repsLoaded,
  setRepsLoaded = () => {}
}) {
  // Ensure surveyQuestions is always an array
  const surveyQuestions = Array.isArray(campaign?.surveyQuestions) 
    ? campaign.surveyQuestions 
    : campaign?.surveyQuestions 
      ? [campaign.surveyQuestions] // If it's a single item but not an array
      : ['What are your thoughts on this topic?']; // Default question if none exists
  
  // Set repsLoaded to true if we have at least one valid question
  React.useEffect(() => {
    if (typeof setRepsLoaded === 'function' && surveyQuestions.length > 0) {
      setRepsLoaded(true);
    }
  }, [surveyQuestions, setRepsLoaded]);

  // Function for video upload/record button
  const handleUploadClick = (e) => {
    e.preventDefault();
    // Find the input and click it
    document.getElementById('upload-video-input').click();
  };

  // Show loading spinner if we don't have survey questions loaded yet
  if (!repsLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <ButtonSpinner color="primary-600" className="h-6 w-6 mb-3" />
          <p className={`text-sm ${
            theme 
              ? theme === 'custom' ? 'text-white/80' : themes[theme]?.subtext || 'text-white/80'
              : 'text-gray-600'
          }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
            Loading survey questions...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Explainer Video Section - only visible if campaign has explainer video */}
      {campaign?.hasExplainerVideo && campaign?.explainerVideo && (
        <div className="mb-8">
          <h2 className={`text-2xl font-bold mb-3 ${
            theme 
              ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
              : 'text-gray-900'
          }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
            Watch This First
          </h2>
          <div className="w-full rounded-xl overflow-hidden relative aspect-video">
            {getYouTubeVideoId(campaign.explainerVideo) && (
              <iframe 
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(campaign.explainerVideo)}`}
                title="Campaign Explainer Video"
                className="absolute top-0 left-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen>
              </iframe>
            )}
          </div>
        </div>
      )}

      {/* Main content with two column layout */}
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-12">
        {/* Questions Panel */}
        <div className="flex-1">
          <h2 className={`text-2xl font-bold ${
            theme 
              ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
              : 'text-gray-900'
          }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
            Questions
          </h2>
          <p className={`mt-2 text-sm ${
            theme 
              ? theme === 'custom' ? 'text-white/80' : themes[theme]?.subtext || 'text-white/80'
              : 'text-gray-600'
          }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
            Choose one or more questions to answer in your video response.
          </p>
          <div className="mt-4 space-y-2 md:mt-6 md:space-y-4">
            {surveyQuestions.map((question, index) => (
              <div
                key={index}
                className={`flex items-start gap-2 rounded-lg border p-3 hover:bg-gray-50 cursor-pointer md:gap-3 md:p-4 ${
                  theme 
                    ? theme === 'custom' 
                      ? 'border-white/20 hover:bg-white/5'
                      : `${themes[theme]?.border || 'border-white/20'} hover:bg-white/5` 
                    : 'border-gray-200'
                }`}
                onClick={() => setCurrentQuestion(index)}
              >
                <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                  theme 
                    ? 'bg-white/10 text-white' 
                    : 'bg-primary-100 text-primary-600'
                }`} style={theme === 'custom' && getTextStyle ? { background: 'rgba(255,255,255,0.1)', ...getTextStyle() } : {}}>
                  {index + 1}
                </div>
                <p className={`text-base ${
                  theme 
                    ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                    : 'text-gray-900'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                  {question}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Video Recording Panel */}
        <div className="flex-1 lg:flex-none lg:w-[420px]">
          <h2 className={`text-2xl font-bold ${
            theme 
              ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
              : 'text-gray-900'
          }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
            Video Response
          </h2>
          <p className={`mt-2 text-sm ${
            theme 
              ? theme === 'custom' ? 'text-white/80' : themes[theme]?.subtext || 'text-white/80'
              : 'text-gray-600'
          }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
            Record a new video or upload an existing one to answer the selected question(s).
          </p>
          <div className="mt-4 md:mt-6">
            {formData.videoUrl ? (
              <div className="relative w-full aspect-video border-2 border-dashed rounded-xl overflow-hidden">
                <video
                  src={formData.videoUrl}
                  className="w-full h-full object-cover rounded-lg"
                  controls
                  playsInline
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      
                      // On iOS/Safari, we need a different approach
                      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                      const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                      
                      if (isSafari || isiOS) {
                        // Create iframe to force download in Safari
                        const iframe = document.createElement('iframe');
                        iframe.style.display = 'none';
                        iframe.src = formData.videoUrl;
                        iframe.onload = function() {
                          // Try to trigger download from the iframe
                          try {
                            const a = document.createElement('a');
                            a.href = formData.videoUrl;
                            a.download = 'advocacy-lab-video.mp4';
                            a.setAttribute('target', '_blank');
                            a.click();
                          } catch (e) {
                            console.error('Download failed:', e);
                          }
                        };
                        document.body.appendChild(iframe);
                        
                        // As a fallback, also open in a new tab where they can save manually
                        window.open(formData.videoUrl, '_blank');
                      } else {
                        // For other browsers, use the download attribute
                        const a = document.createElement('a');
                        a.href = formData.videoUrl;
                        a.download = 'advocacy-lab-video.mp4';
                        
                        // Try adding referrerPolicy and a unique timestamp to avoid browser caching issues
                        a.referrerPolicy = 'no-referrer';
                        const timestamp = new Date().getTime();
                        a.href = `${formData.videoUrl}${formData.videoUrl.includes('?') ? '&' : '?'}_t=${timestamp}`;
                        
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }
                    }}
                    className="p-2 rounded-full bg-gray-900/80 text-white backdrop-blur-sm hover:bg-gray-800"
                    title="Download video"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-4 w-4" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveVideo}
                    className="p-2 rounded-full bg-gray-900/80 text-white backdrop-blur-sm hover:bg-gray-800"
                    title="Remove video"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className={`border-2 border-dashed rounded-xl cursor-pointer hover:bg-gray-50 ${
                theme 
                  ? theme === 'custom'
                    ? 'border-white/20 hover:bg-white/5'
                    : `${themes[theme]?.border || 'border-white/20'} hover:bg-white/5`
                  : 'border-gray-300'
              }`}>
                <div className="flex flex-col items-center justify-center p-6 text-center">
                  <div className={`mb-4 rounded-full p-3 ${
                    theme 
                      ? theme === 'custom'
                        ? 'bg-white/10'
                        : themes[theme]?.input || 'bg-white/10'
                      : 'bg-gray-100'
                  }`}>
                    <Video className={`w-6 h-6 ${
                      theme 
                        ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                        : 'text-gray-500'
                    }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}} />
                  </div>
                  <p className={`text-base font-medium ${
                    theme 
                      ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                      : 'text-gray-900'
                  }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                    Add your video response
                  </p>
                  <div className={`mt-1 text-xs ${
                    theme 
                      ? theme === 'custom' ? 'text-white/80' : themes[theme]?.subtext || 'text-white/80'
                      : 'text-gray-500'
                  }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
                    <p className="font-medium">Video must be between 10 seconds and 2 minutes</p>
                  </div>
                  
                  {/* Single combined button for recording/uploading */}
                  <div className="mt-6">
                    <button 
                      type="button"
                      onClick={handleUploadClick} /* Using the non-capture version to allow both upload and record */
                      className={`flex items-center gap-2 px-6 py-3 rounded-md w-full justify-center ${
                        theme 
                          ? theme === 'custom' 
                            ? 'bg-white/20 text-white hover:bg-white/30' 
                            : 'bg-white/20 text-white hover:bg-white/30'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                      style={theme === 'custom' && getTextStyle ? { background: 'rgba(255,255,255,0.2)', ...getTextStyle() } : {}}
                    >
                      <Video className="w-5 h-5" />
                      <span>Upload or Record Video</span>
                    </button>
                  </div>
                </div>
                {/* Hidden inputs for recording and uploading */}
                <input
                  id="upload-video-input"
                  type="file"
                  className="hidden"
                  accept="video/mp4,video/quicktime,video/x-m4v"
                  onChange={handleVideoUpload}
                  /* The absence of the "capture" attribute allows both recording and selecting from library */
                />
              </div>
            )}
            {videoDuration && formData.videoUrl && (
              <div className={`mt-2 text-sm ${
                theme 
                  ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                  : 'text-gray-600'
              }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                <div className="flex items-center gap-2">
                  <span>Video length: {Math.floor(videoDuration / 60)}:{String(videoDuration % 60).padStart(2, '0')}</span>
                  {videoDuration >= 10 && videoDuration <= 120 && (
                    <div className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      theme 
                        ? theme === 'custom' 
                          ? 'bg-white/10 text-white' 
                          : 'bg-white/10 text-white'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      Valid Length
                    </div>
                  )}
                  {videoDuration > 120 && (
                    <div className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      theme 
                        ? theme === 'custom' 
                          ? 'bg-white/20 text-white' 
                          : 'bg-white/20 text-white'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      Too Long (max 2 min)
                    </div>
                  )}
                  {videoDuration < 10 && (
                    <div className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      theme 
                        ? theme === 'custom' 
                          ? 'bg-white/20 text-white' 
                          : 'bg-white/20 text-white'
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      Too Short (min 10 sec)
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Video guidelines - Full width section moved outside the column layout */}
      <div className="mt-6">
        <div className={`p-3 rounded-md ${
          theme 
            ? theme === 'custom'
              ? 'bg-white/10 border border-white/20'
              : 'bg-white/10 border border-white/20'
            : 'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              theme 
                ? theme === 'custom' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-white/20 text-white'
                : 'bg-blue-100 text-blue-800'
            }`}>
              Video Guidelines
            </div>
          </div>
          
          <div className={`space-y-1.5 text-xs ${
            theme 
              ? theme === 'custom' ? 'text-white/80' : 'text-white/80'
              : 'text-blue-700'
          }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
            <p className="flex items-start">
              <span className="mr-1.5">•</span>
              <span>Videos must be 10 seconds to 2 minutes in length</span>
            </p>
            <p className="flex items-start">
              <span className="mr-1.5">•</span>
              <span>Record a new video or select one from your device</span>
            </p>
            <p className="flex items-start">
              <span className="mr-1.5">•</span>
              <span>Please ensure content is appropriate and follows community standards. Do not include offensive language, hate speech, explicit content or copyrighted material.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}